/**
 * CascadingSelectComponent
 * Dependent select dropdowns where options in child levels depend on parent selection
 */

import React, { useCallback, useMemo, useState, memo } from 'react';
import type { FormControlProps } from '../../types';

interface SelectOption {
  id: string;
  label: string;
}

interface CascadeLevel {
  id: string;
  label: string;
  sourceUrl?: string;
  parentId?: string;
}

interface CascadingSelectComponentProps extends FormControlProps {
  levels?: CascadeLevel[];
  dataSource?: Record<string, SelectOption[]>;
  apiEndpoint?: string;
  onChange?: (selections: Record<string, string>) => void;
}

export const CascadingSelectComponent: React.FC<CascadingSelectComponentProps> = memo(({
  value = {},
  levels = [],
  dataSource = {},
  apiEndpoint = '',
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `cascading-select-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const [selections, setSelections] = useState<Record<string, string>>(
    typeof value === 'object' ? (value as Record<string, string>) : {},
  );

  const handleLevelChange = useCallback(
    async (levelId: string, selectedId: string) => {
      const newSelections = { ...selections };
      newSelections[levelId] = selectedId;

      // Clear dependent levels
      const levelIndex = levels.findIndex((l) => l.id === levelId);
      for (let i = levelIndex + 1; i < levels.length; i++) {
        delete newSelections[levels[i].id];
      }

      setSelections(newSelections);
      onChange?.(newSelections);
    },
    [selections, levels, onChange],
  );

  const getOptionsForLevel = useCallback(
    (level: CascadeLevel): SelectOption[] => {
      if (!level.parentId) {
        return dataSource[level.id] || [];
      }

      const parentId = level.parentId;
      const parentSelection = selections[parentId];

      if (!parentSelection) return [];

      // If we have a direct data source, use it
      const key = `${parentId}:${parentSelection}`;
      return dataSource[key] || dataSource[level.id] || [];
    },
    [dataSource, selections],
  );

  return (
    <div className="af-form-field__group cascading-select-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="cascading-select__container">
        {levels.map((level, idx) => {
          const options = getOptionsForLevel(level);
          const isDisabled = disabled || !!(level.parentId && !selections[level.parentId]);

          return (
            <div key={level.id} className="cascading-select__level">
              <label htmlFor={`${inputId}-${level.id}`} className="cascading-select__level-label">
                {level.label}
              </label>
              <select
                id={`${inputId}-${level.id}`}
                value={selections[level.id] || ''}
                onChange={(e) => handleLevelChange(level.id, e.target.value)}
                disabled={isDisabled}
                className="cascading-select__select"
                aria-label={level.label}
              >
                <option value="">Select {level.label}</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
